import { hashKey, useQuery } from '@tanstack/react-query';
import { head, isEmpty, uniqBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useDebouncedValueSafe } from 'src/hooks/useDebouncedValueSafe';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getSectionInfoDisplay } from 'src/utils/autoPricingUtils';
import { filterSectionByZone } from 'src/utils/venueConfigUtils';
import {
  Feature,
  ListingClient,
  ListingDetailsPricingUpdates,
  ListingPriceCalculation,
  SectionInfo,
  VenueMapInfo,
} from 'src/WebApiController';

export type ListingPricingInputWithId = ListingDetailsPricingUpdates & {
  id: number;
};

export const useAutoPricingPreview = (
  viagogoEventId: number | null,
  listingId?: number | undefined,
  listingInput?: ListingPricingInputWithId
) => {
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const [debouncedInput] = useDebouncedValueSafe(listingInput, 300);

  const { trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();

  const shouldQuery = useMemo(() => {
    if (
      activeAccountWebClientConfig.activeAccountId == null ||
      !hasAutoPricingFeature
    ) {
      return false;
    }
    if (viagogoEventId == null || listingId == null || debouncedInput == null) {
      return false;
    }
    return listingId === debouncedInput.id;
  }, [
    activeAccountWebClientConfig.activeAccountId,
    hasAutoPricingFeature,
    listingId,
    debouncedInput,
    viagogoEventId,
  ]);

  const priceCalcQuery = useQuery({
    queryKey: [
      'ListingClient.getListingPriceCalculation',
      viagogoEventId,
      hashKey([listingId, debouncedInput]),
    ],
    queryFn: async () => {
      if (!shouldQuery || isEmpty(debouncedInput)) {
        return null;
      }
      const { id: inputListingId, ...input } = debouncedInput;
      if (inputListingId !== listingId) {
        return null;
      }

      const r = await new ListingClient(
        activeAccountWebClientConfig
      ).getListingPriceCalculation(
        viagogoEventId ?? undefined,
        listingId ?? undefined,
        input!
      );
      return r;
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    // The response of the price calculation is huge
    // We don't want to cache the data and eat up the heap mem as the query is per input
    gcTime: 0,
    meta: {
      persist: false,
      onError: (error: ErrorTypes) => {
        trackError(
          'ListingClient.getListingPriceCalculation',
          error,
          debouncedInput
        );
      },
    },
  });

  return { priceCalcQuery };
};

export const useVenueMapHandlers = (
  filterSections: SectionInfo[],
  pricingPreview?: ListingPriceCalculation,
  venueMapInfo?: VenueMapInfo | null,
  onSelectedSectionsChange?: (selectedSections: SectionInfo[]) => void,
  onToggleRecommendingSectionIds?: (
    sectionIds: number[],
    option?: { include?: boolean; exclude?: boolean }
  ) => void
) => {
  const onShiftSelect = useCallback(
    (sectionInfo: SectionInfo) => {
      // We are using the ticketClassId to group the sections
      const selectedClassId = head(
        sectionInfo?.rows
          ?.map(({ tktClass: ticketClass }) => ticketClass?.id)
          .filter((id) => !!id)
      );
      const classSections =
        venueMapInfo?.sections?.filter(filterSectionByZone(selectedClassId)) ||
        [];

      const classedSectionIds = classSections.map(({ id }) => id);
      const filteredSeciontIds = filterSections.map(({ id }) => id);
      let cloned = [...filterSections];
      if (classedSectionIds.every((id) => filteredSeciontIds.includes(id))) {
        cloned = cloned.filter(({ id }) => !classedSectionIds.includes(id));
        onToggleRecommendingSectionIds?.(classedSectionIds, { include: true });
      } else {
        for (const section of classSections) {
          if (!filteredSeciontIds.includes(section.id)) {
            cloned.push(section);
          }
        }
        onToggleRecommendingSectionIds?.(classedSectionIds, { exclude: true });
      }
      onSelectedSectionsChange?.(cloned);
    },
    [
      filterSections,
      onSelectedSectionsChange,
      onToggleRecommendingSectionIds,
      venueMapInfo?.sections,
    ]
  );

  const onSingleSelect = useCallback(
    (section: SectionInfo) => {
      const idx = filterSections.findIndex(({ id }) => id === section.id);
      const cloned = [...filterSections];
      if (idx === -1) {
        cloned.push(section);
      } else {
        cloned.splice(idx, 1);
      }
      onToggleRecommendingSectionIds?.([section.id]);
      onSelectedSectionsChange?.(cloned);
    },
    [filterSections, onSelectedSectionsChange, onToggleRecommendingSectionIds]
  );

  const onSectionClicked = useCallback(
    (e: MouseEvent, sectionInfo: SectionInfo) => {
      if (e.shiftKey) {
        onShiftSelect(sectionInfo);
        return;
      }

      onSingleSelect(sectionInfo);
    },
    [onSingleSelect, onShiftSelect]
  );

  const onToggleMirrors = useCallback(
    (mirrors: SectionInfo[], exclude?: boolean) => {
      if (exclude) {
        const idsToExclude = mirrors.map(({ id }) => id);
        const updated = uniqBy(
          filterSections.filter(({ id }) => !idsToExclude.includes(id)),
          'id'
        ).toSorted((a, b) => a.name.localeCompare(b.name));
        onSelectedSectionsChange?.(updated);
        onToggleRecommendingSectionIds?.(idsToExclude, { include: true });
      } else {
        const updated = uniqBy(filterSections.concat(mirrors), 'id').toSorted(
          (a, b) => a.name.localeCompare(b.name)
        );
        onSelectedSectionsChange?.(updated);
        onToggleRecommendingSectionIds?.(
          mirrors.map(({ id }) => id),
          { exclude: true }
        );
      }
    },
    [filterSections, onSelectedSectionsChange, onToggleRecommendingSectionIds]
  );

  const onSectionHovered = useCallback(
    (hoveredSection: SectionInfo) => {
      return getSectionInfoDisplay(
        hoveredSection,
        pricingPreview?.compListings ?? [],
        venueMapInfo?.sectionScores
      );
    },
    [pricingPreview?.compListings, venueMapInfo?.sectionScores]
  );

  const onSectionTapAndHold = useCallback(
    (e: MouseEvent | TouchEvent, sectionInfo: SectionInfo) => {
      e.stopPropagation();
      e.preventDefault();
      // Same logic as shift+click - select all sections in the same zone
      const selectedClassId = head(
        sectionInfo?.rows
          ?.map(({ tktClass: ticketClass }) => ticketClass?.id)
          .filter((id) => !!id)
      );
      const classSections =
        venueMapInfo?.sections?.filter(filterSectionByZone(selectedClassId)) ||
        [];

      const classedSectionIds = classSections.map(({ id }) => id);
      const filteredSectionIds = filterSections.map(({ id }) => id);
      let cloned = [...filterSections];

      if (classedSectionIds.every((id) => filteredSectionIds.includes(id))) {
        // If all sections in zone are selected, deselect them
        cloned = cloned.filter(({ id }) => !classedSectionIds.includes(id));
        onToggleRecommendingSectionIds?.(classedSectionIds, { include: true });
      } else {
        // Select all sections in the zone
        for (const section of classSections) {
          if (!filteredSectionIds.includes(section.id)) {
            cloned.push(section);
          }
        }
        onToggleRecommendingSectionIds?.(classedSectionIds, { exclude: true });
      }
      onSelectedSectionsChange?.(cloned);
    },
    [
      filterSections,
      onSelectedSectionsChange,
      onToggleRecommendingSectionIds,
      venueMapInfo?.sections,
    ]
  );

  return {
    onSectionClicked,
    onToggleMirrors,
    onSectionHovered,
    onSectionTapAndHold,
  };
};
